import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Loader2, BookMarked, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DELIVERY_STATUS_LABELS,
  DELIVERY_STATUSES,
  SUPPORT_TYPES,
  reflectionRequired,
  useCreateDeliveryEvent,
  useUpdateDeliveryEvent,
  type ProgramItem,
  type ProgramDeliveryEvent,
  type DiarySearchEntry,
} from "@/lib/program-registration";
import { DiaryEntryPicker } from "./diary-entry-picker";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";


type SourceMode = "manual_entry" | "diary_entry";

interface DeliveryEventFormProps {
  open: boolean;
  onClose: () => void;
  registerId: number;
  item: ProgramItem;
  event?: ProgramDeliveryEvent;
}

export function DeliveryEventForm({
  open,
  onClose,
  registerId,
  item,
  event,
}: DeliveryEventFormProps) {
  const { toast } = useToast();
  const isEditing = !!event;

  const today = format(new Date(), "yyyy-MM-dd");

  // Source mode — diary_entry when event already had a linked entry
  const [sourceMode, setSourceMode] = useState<SourceMode>(
    event?.sourceType === "diary_entry" ? "diary_entry" : "manual_entry",
  );
  const [linkedEntry, setLinkedEntry] = useState<DiarySearchEntry | null>(null);
  const [linkedEntryId, setLinkedEntryId] = useState<number | null>(
    event?.diaryEntryId ?? null,
  );

  const [deliveryDate, setDeliveryDate] = useState(event?.deliveryDate ?? today);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState(
    event?.deliveryStatus ?? "delivered_as_planned",
  );
  const [reflectionNote, setReflectionNote] = useState(event?.reflectionNote ?? "");
  const [actualContent, setActualContent] = useState(event?.actualContentDelivered ?? "");
  const [remainingContent, setRemainingContent] = useState(
    event?.remainingContentNote ?? "",
  );
  const [sessionLabel, setSessionLabel] = useState(event?.manualSessionLabel ?? "");
  const [supportTypes, setSupportTypes] = useState<string[]>(
    event?.differentiationSupportTypes ?? [],
  );
  const [reflectionTouched, setReflectionTouched] = useState(false);

  const createEvent = useCreateDeliveryEvent(registerId, item.id);
  const updateEvent = useUpdateDeliveryEvent(registerId, event?.id ?? 0);
  const isPending = isEditing ? updateEvent.isPending : createEvent.isPending;

  const needsReflection = reflectionRequired(deliveryStatus);
  const reflectionError =
    needsReflection && reflectionTouched && !reflectionNote.trim();

  function resetForm() {
    setSourceMode(
      event?.sourceType === "diary_entry" ? "diary_entry" : "manual_entry",
    );
    setLinkedEntry(null);
    setLinkedEntryId(event?.diaryEntryId ?? null);
    setDeliveryDate(event?.deliveryDate ?? today);
    setDeliveryStatus(event?.deliveryStatus ?? "delivered_as_planned");
    setReflectionNote(event?.reflectionNote ?? "");
    setActualContent(event?.actualContentDelivered ?? "");
    setRemainingContent(event?.remainingContentNote ?? "");
    setSessionLabel(event?.manualSessionLabel ?? "");
    setSupportTypes(event?.differentiationSupportTypes ?? []);
    setReflectionTouched(false);
  }

  function handleClose() {
    resetForm();
    onClose();
  }

  function toggleSupport(type: string) {
    setSupportTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  }

  function handleStatusChange(status: string) {
    setDeliveryStatus(status);
    setReflectionTouched(false);
  }

  function handleSourceMode(mode: SourceMode) {
    setSourceMode(mode);
    if (mode === "manual_entry") {
      // Clear diary link when switching back
      setLinkedEntry(null);
      setLinkedEntryId(null);
    }
  }

  function handleDiarySelect(entry: DiarySearchEntry | null) {
    if (entry === null) {
      setLinkedEntry(null);
      setLinkedEntryId(null);
      return;
    }
    setLinkedEntry(entry);
    setLinkedEntryId(entry.id);
    // Autofill date from diary entry
    setDeliveryDate(entry.date);
    // Autofill "what was delivered" from diary content (if currently empty)
    if (!actualContent.trim() && entry.contentForAutofill) {
      setActualContent(entry.contentForAutofill);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (needsReflection) {
      setReflectionTouched(true);
      if (!reflectionNote.trim()) return;
    }

    // Validate diary link when in diary_entry mode
    if (sourceMode === "diary_entry" && !linkedEntryId) {
      toast({
        title: "No diary entry selected",
        description: "Select a diary entry from the list, or switch to manual entry.",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      sourceType: sourceMode,
      diaryEntryId: sourceMode === "diary_entry" ? linkedEntryId : null,
      deliveryDate,
      deliveryStatus,
      reflectionNote: reflectionNote.trim() || null,
      actualContentDelivered: actualContent.trim() || null,
      remainingContentNote: remainingContent.trim() || null,
      manualSessionLabel: sessionLabel.trim() || null,
      differentiationSupportTypes: supportTypes.length > 0 ? supportTypes : null,
    };

    const callbacks = {
      onSuccess: () => {
        toast({ title: isEditing ? "Delivery event updated" : "Delivery event saved" });
        handleClose();
      },
      onError: (err: unknown) => {
        toast({
          title: isEditing
            ? "Couldn't update delivery event"
            : "Couldn't save delivery event",
          description: getApiErrorMessage(err, "Please check your entries and try again."),
          variant: "destructive",
        });
      },
    };

    if (isEditing) {
      updateEvent.mutate(payload, callbacks);
    } else {
      createEvent.mutate(payload, callbacks);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold leading-tight">
            {isEditing ? "Edit Delivery" : "Log Delivery"} — {item.title}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-1">
          {/* Source mode toggle */}
          <div className="space-y-2">
            <Label>Source</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleSourceMode("manual_entry")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                  sourceMode === "manual_entry"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-input bg-background text-muted-foreground hover:bg-muted/50"
                }`}
              >
                <PenLine className="h-3.5 w-3.5" />
                Manual entry
              </button>
              <button
                type="button"
                onClick={() => handleSourceMode("diary_entry")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                  sourceMode === "diary_entry"
                    ? "border-primary bg-primary/5 text-primary"
                    : "border-input bg-background text-muted-foreground hover:bg-muted/50"
                }`}
              >
                <BookMarked className="h-3.5 w-3.5" />
                Link from diary
              </button>
            </div>

            {/* Diary picker — shown in diary_entry mode */}
            {sourceMode === "diary_entry" && (
              <DiaryEntryPicker
                linkedEntry={linkedEntry}
                linkedEntryId={linkedEntryId}
                fallbackDate={deliveryDate}
                onSelect={handleDiarySelect}
                onClearById={() => {
                  setLinkedEntry(null);
                  setLinkedEntryId(null);
                }}
              />
            )}
          </div>

          {/* Date + session label */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>
                Delivery date *
                {sourceMode === "diary_entry" && (linkedEntry ?? linkedEntryId) && (
                  <span className="ml-1 text-xs text-muted-foreground font-normal">
                    (from diary)
                  </span>
                )}
              </Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
                    {deliveryDate
                      ? format(new Date(deliveryDate + "T12:00:00"), "d MMM yyyy")
                      : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={
                      deliveryDate ? new Date(deliveryDate + "T12:00:00") : undefined
                    }
                    onSelect={(d) => {
                      if (d) setDeliveryDate(format(d, "yyyy-MM-dd"));
                      setCalendarOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sessionLabel">Session label (optional)</Label>
              <Input
                id="sessionLabel"
                placeholder="e.g. Period 3, Lesson 7"
                value={sessionLabel}
                onChange={(e) => setSessionLabel(e.target.value)}
              />
            </div>
          </div>

          {/* Delivery status */}
          <div className="space-y-2">
            <Label>Delivery status *</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {DELIVERY_STATUSES.map((status) => {
                const active = deliveryStatus === status;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => handleStatusChange(status)}
                    className={`text-left px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                      active
                        ? "border-primary bg-primary/5 text-primary"
                        : "border-input bg-background text-foreground hover:bg-muted/50"
                    }`}
                  >
                    {DELIVERY_STATUS_LABELS[status]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Reflection note */}
          <div className="space-y-1.5">
            <Label htmlFor="reflection">
              Reflection note
              {needsReflection ? (
                <span className="text-destructive ml-1">*</span>
              ) : (
                <span className="text-muted-foreground text-xs ml-1">
                  (optional for "Completed as planned")
                </span>
              )}
            </Label>
            <Textarea
              id="reflection"
              rows={3}
              placeholder={
                needsReflection
                  ? "Required — what happened and why?"
                  : "Optional notes about this delivery"
              }
              value={reflectionNote}
              onChange={(e) => setReflectionNote(e.target.value)}
              onBlur={() => needsReflection && setReflectionTouched(true)}
              className={reflectionError ? "border-destructive" : ""}
            />
            {reflectionError && (
              <p className="text-xs text-destructive">
                A reflection note is required for this delivery status.
              </p>
            )}
          </div>

          {/* What was actually delivered */}
          <div className="space-y-1.5">
            <Label htmlFor="actualContent">
              What was actually delivered{" "}
              <span className="text-muted-foreground text-xs">(optional)</span>
              {sourceMode === "diary_entry" && linkedEntry && (
                <span className="ml-1 text-xs text-muted-foreground font-normal">
                  — pre-filled from diary
                </span>
              )}
            </Label>
            <Textarea
              id="actualContent"
              rows={3}
              placeholder="Describe what content was covered in the session"
              value={actualContent}
              onChange={(e) => setActualContent(e.target.value)}
            />
          </div>

          {/* Remaining content */}
          {(deliveryStatus === "partially_delivered" ||
            deliveryStatus === "continued_from_previous_lesson") && (
            <div className="space-y-1.5">
              <Label htmlFor="remainingContent">
                Remaining content note{" "}
                <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
              <Textarea
                id="remainingContent"
                rows={2}
                placeholder="What still needs to be covered?"
                value={remainingContent}
                onChange={(e) => setRemainingContent(e.target.value)}
              />
            </div>
          )}

          {/* Support / differentiation types */}
          <div className="space-y-2">
            <Label>
              Support &amp; differentiation used{" "}
              <span className="text-muted-foreground text-xs">
                (optional — select all that apply)
              </span>
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SUPPORT_TYPES.map((type) => (
                <label
                  key={type}
                  className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-muted/50 transition-colors"
                >
                  <Checkbox
                    checked={supportTypes.includes(type)}
                    onCheckedChange={() => toggleSupport(type)}
                    className="flex-shrink-0"
                  />
                  <span className="text-sm leading-snug">{type}</span>
                </label>
              ))}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Save changes" : "Save delivery event"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
